import React from "react";

// Phase 3D.3-C - Presentational card for the SINGLE deterministic marketing
// recommendation produced by buildGrowthRecommendation(opportunityResult).
//
// PURE PRESENTATION ONLY:
//   * Renders EXCLUSIVELY the fields returned by the 3D.3-B engine.
//   * NO KPI recalculation, NO API/network call, NO env read, NO hardcoded
//     market/figure/period/evidence text.
//   * NO second market selection, NO fabricated default recommendation.
//   * NO budget / channel / media / ROI / expected result / projection.
//   * NO inferred cause of the growth.
//   * NO "launch/publish/activate" control (recommendation != campaign).
//   * Context (stay / spend / currency) is shown as CONTEXT ONLY and never
//     changes the decisional rendering.
//   * Demo provenance badge only; never a Simulation badge (simulation===false).

import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";
import { MISSING, periodLabel } from "@/lib/savoieKpis";

// Human-readable labels for the closed action-type enum (no activation semantics).
const ACTION_LABELS = {
  audience_focus: "Concentration de l'attention marketing",
};

const MONITORING_NOTE =
  "Période de suivi analytique uniquement — ce n'est ni une date de campagne ni une projection.";

// --- Visual-only formatters (fr-FR). Never used for any decision. ---------
const nf = new Intl.NumberFormat("fr-FR");
const fmtInt = (v) => (typeof v === "number" ? nf.format(Math.round(v)) : MISSING);
const fmtPct1 = (v) =>
  typeof v === "number" ? `${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %` : MISSING;
const fmtPctSigned = (v) =>
  typeof v === "number"
    ? `${v >= 0 ? "+" : ""}${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} %`
    : MISSING;
const fmtStay = (v) =>
  typeof v === "number" ? `${(Math.round(v * 10) / 10).toFixed(1).replace(".", ",")} nuits` : MISSING;
const fmtSpend = (v, currency) => {
  if (typeof v !== "number") return MISSING;
  const n = fmtInt(v);
  return currency === "EUR" ? `${n} EUR` : n;
};

// "2024-12" -> "décembre 2024" (reuses the pure label helper; no KPI recompute).
function frPeriod(iso) {
  if (typeof iso !== "string") return MISSING;
  const [y, m] = iso.split("-").map((x) => Number(x));
  if (!Number.isFinite(y) || !Number.isFinite(m)) return MISSING;
  return periodLabel({ year: y, month: m });
}

function DataRow({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-slate-100 py-1.5 first:border-t-0">
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900" data-testid={testid}>{value}</dd>
    </div>
  );
}

export default function RecommendationCard({ recommendationResult }) {
  // Fail-closed: never fabricate a default recommendation.
  if (!recommendationResult || recommendationResult.status !== "recommended") return null;

  const r = recommendationResult;
  const ev = r.evidence_used || {};
  const ctx = r.context_only || {};
  const actionLabel = ACTION_LABELS[r.action_type] || r.action_type;

  return (
    <section
      className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-5 shadow-sm"
      aria-labelledby="recommendation-title"
      data-testid="recommendation-card"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id="recommendation-title"
          className="font-heading text-lg font-bold tracking-tight text-indigo-900 sm:text-xl"
          data-testid="recommendation-title"
        >
          Recommandation déterministe
        </h2>
        {/* Demo badge from explicit provenance; no Simulation badge (simulation === false). */}
        <ProvenanceBadges meta={r.meta} />
      </div>

      {/* Action type (human-readable), never an activation control. */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
          {"Type d'action"}
        </span>
        <span
          className="inline-flex items-center rounded-full border border-indigo-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-indigo-800"
          data-testid="recommendation-action"
        >
          {actionLabel}
        </span>
      </div>

      {/* Objective: taken verbatim from the engine output. */}
      <p className="mt-3 text-sm leading-relaxed text-slate-800" data-testid="recommendation-objective">
        {r.objective}
      </p>

      {/* Audience: the market carried from the opportunity (no audience size). */}
      <p className="mt-2 text-sm text-slate-700" data-testid="recommendation-audience">
        Audience : <span className="font-semibold">{r.audience?.source_market_name}</span>
        {" "}({r.audience?.source_market_code})
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* ZONE 1 - Evidence used (from the opportunity, not recomputed) */}
        <div
          className="rounded-xl border border-indigo-200 bg-white p-4"
          data-testid="recommendation-evidence"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
            Preuves utilisées
          </h3>
          <dl>
            <DataRow label="Période observée" value={frPeriod(r.evidence_period)} testid="rec-ev-period" />
            <DataRow
              label="Période de comparaison"
              value={frPeriod(r.comparison_period)}
              testid="rec-ev-comparison"
            />
            <DataRow
              label="Arrivées visiteurs"
              value={fmtInt(ev.visitor_arrivals)}
              testid="rec-ev-arrivals"
            />
            <DataRow
              label="Arrivées (période comparable)"
              value={fmtInt(ev.previous_year_visitor_arrivals)}
              testid="rec-ev-prev-arrivals"
            />
            <DataRow
              label="Croissance des arrivées"
              value={fmtPctSigned(ev.growth_percent)}
              testid="rec-ev-growth"
            />
            <DataRow
              label="Part du marché"
              value={fmtPct1(ev.market_share_percent)}
              testid="rec-ev-share"
            />
          </dl>

          {/* Analytical follow-up period (NOT a campaign date / NOT a projection). */}
          <div className="mt-3 rounded-lg bg-indigo-50 p-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-slate-600">Période de suivi analytique</span>
              <span className="text-sm font-semibold text-slate-900" data-testid="rec-monitoring-period">
                {frPeriod(r.proposed_monitoring_period)}
              </span>
            </div>
            <p className="mt-1 text-xs italic text-slate-500" data-testid="recommendation-monitoring-note">
              {MONITORING_NOTE}
            </p>
          </div>
        </div>

        {/* ZONE 2 - Context, explicitly NOT used to produce the recommendation */}
        <div
          className="rounded-xl border border-slate-200 bg-white p-4"
          data-testid="recommendation-context"
        >
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Contexte — non utilisé pour produire la recommandation
          </h3>
          <dl>
            <DataRow
              label="Durée moyenne de séjour"
              value={fmtStay(ctx.average_stay)}
              testid="rec-ctx-stay"
            />
            <DataRow
              label="Dépense moyenne / visiteur"
              value={fmtSpend(ctx.tourism_spend_per_visitor, ctx.currency)}
              testid="rec-ctx-spend"
            />
          </dl>
          <p className="mt-2 text-xs italic text-slate-500" data-testid="recommendation-context-note">
            Contexte uniquement — non utilisé pour produire la recommandation.
          </p>
        </div>
      </div>

      <p className="mt-4 text-xs text-slate-500" data-testid="recommendation-disclaimer">
        {r.disclaimer}
      </p>
    </section>
  );
}
